/**
 * Settings → Account & sign-in (design Account board): "Email addresses" (primary, verified and waiting-for-code
 * sign-in emails: add + verify by code, make primary, remove) and "Password & sign-in" (password, passkeys, two-step
 * verification with QR setup and backup codes). Every change flashes Saved next to its group. One sheet at a time for
 * the multi-step changes; proof (current password or an emailed code) is asked inside the sheet that needs it.
 * Renders AccountSecurityViewModel.
 */
import { AccountSecurityLogic, backupCodesFile, type AccountSecurityState, type AccountSecurityViewModel, type ProofState } from "@bundle/core";
import type { AccountEmail, AccountPasskey } from "@bundle/contracts";
import { useEffect, useState, type ReactNode } from "react";
import { Banner, Button, Checkbox, Menu, QrCode, Sheet, Stack, Text, TextField } from "../../primitives/index.ts";
import { useFormat, useT, useTr } from "../../i18n/I18nProvider.tsx";
import { FieldNote, SavedMark, SectionStatus, SettingsGroup, SettingsRow, useSavedFlash } from "./SettingsKit.tsx";

export type AccountSecuritySettingsProps = {
  vm: AccountSecurityViewModel;
  state: AccountSecurityState;
  /** Copies text (backup codes, the authenticator key). */
  onCopy: (text: string) => void;
  /** Saves a text file (backup codes). */
  onSaveFile?: (name: string, text: string) => void;
};

export function AccountSecuritySettings({ vm, state: s, onCopy, onSaveFile }: AccountSecuritySettingsProps) {
  const t = useT();
  const tr = useTr();
  const [flashing, flash] = useSavedFlash();
  useEffect(() => {
    if (s.saved.n > 0) flash();
  }, [s.saved.n, flash]);
  if (s.status !== "ready") {
    return <SectionStatus status={s.status === "error" ? "error" : "loading"} onRetry={() => void vm.load()} testID="account-security-status" />;
  }
  const saved = (group: NonNullable<AccountSecurityState["saved"]["group"]>) => <SavedMark shown={flashing && s.saved.group === group} testID={`account-security-saved-${group}`} />;
  return (
    <Stack gap="6" testID="account-security">
      {s.info === undefined ? null : <Banner tone="success" message={tr(s.info)} onDismiss={() => vm.dismissInfo()} dismissLabel={t("common.close")} testID="account-security-info" />}
      <Stack gap="1">
        <SettingsGroup
          title={t("settings.signIn.emails.title")}
          action={{ label: t("settings.signIn.emails.add"), onPress: () => vm.startAddEmail(), testID: "account-emails-add" }}
          foot={t("settings.signIn.emails.foot")}
          testID="account-emails"
        >
          {s.emails.map((e) => (
            <EmailRow key={e.id} vm={vm} email={e} />
          ))}
        </SettingsGroup>
        {saved("emails")}
      </Stack>
      <Stack gap="1">
        <SettingsGroup title={t("settings.signIn.methods.title")} testID="account-methods">
          <SettingsRow
            label={t("settings.signIn.password.label")}
            sub={s.hasPassword ? t("settings.signIn.password.subOn") : t("settings.signIn.password.subOff")}
            trailing={
              <Stack direction="horizontal" gap="2">
                {s.hasPassword && s.canRemovePassword ? (
                  <Button label={t("settings.signIn.password.remove")} variant="ghost" size="sm" onPress={() => vm.startPassword("remove")} testID="account-password-remove" />
                ) : null}
                <Button
                  label={s.hasPassword ? t("settings.signIn.password.change") : t("settings.signIn.password.set")}
                  variant="secondary"
                  size="sm"
                  onPress={() => vm.startPassword(s.hasPassword ? "change" : "set")}
                  testID="account-password-edit"
                />
              </Stack>
            }
            testID="account-password"
          />
          <SettingsRow
            label={t("settings.signIn.passkeys.label")}
            sub={t("settings.signIn.passkeys.sub")}
            trailing={<Button label={t("settings.signIn.passkeys.add")} variant="secondary" size="sm" icon="plus" onPress={() => vm.startAddPasskey()} testID="account-passkeys-add" />}
            testID="account-passkeys"
          />
          {s.passkeys.map((p) => (
            <PasskeyRow key={p.id} vm={vm} passkey={p} canRemove={s.canRemovePasskey} />
          ))}
          <SettingsRow
            label={t("settings.signIn.twoFactor.label")}
            sub={s.twoFactorEnabled ? t("settings.signIn.twoFactor.subOn", { count: s.backupCodesRemaining }) : t("settings.signIn.twoFactor.subOff")}
            {...(s.twoFactorEnabled ? { tag: { label: t("settings.signIn.twoFactor.on"), tone: "success" as const } } : {})}
            trailing={
              s.twoFactorEnabled ? (
                <Menu
                  items={[
                    { id: "codes", icon: "key", label: t("settings.signIn.twoFactor.newCodes"), onSelect: () => vm.startNewBackupCodes() },
                    { id: "off", icon: "close", label: t("settings.signIn.twoFactor.turnOff"), destructive: true, onSelect: () => vm.startTwoFactorOff() },
                  ]}
                  testID="account-two-factor-menu"
                >
                  <Button label={t("settings.signIn.twoFactor.manage")} variant="secondary" size="sm" onPress={() => {}} testID="account-two-factor-manage" />
                </Menu>
              ) : (
                <Button label={t("settings.signIn.twoFactor.setUp")} variant="secondary" size="sm" onPress={() => vm.startTwoFactor()} testID="account-two-factor-setup" />
              )
            }
            testID="account-two-factor"
          />
        </SettingsGroup>
        {saved("password")}
        {saved("passkeys")}
        {saved("twoFactor")}
      </Stack>
      <SecuritySheet vm={vm} state={s} onCopy={onCopy} onSaveFile={onSaveFile} />
    </Stack>
  );
}

function EmailRow({ vm, email: e }: { vm: AccountSecurityViewModel; email: AccountEmail }) {
  const t = useT();
  const tag = e.primary
    ? { label: t("settings.signIn.emails.primary"), tone: "accent" as const }
    : e.verified
      ? { label: t("settings.signIn.emails.verified"), tone: "success" as const }
      : { label: t("settings.signIn.emails.pending"), tone: "neutral" as const };
  const sub = e.primary ? t("settings.signIn.emails.primarySub") : e.verified ? t("settings.signIn.emails.secondarySub") : t("settings.signIn.emails.pendingSub");
  const id = e.primary ? "primary" : e.id;
  return (
    <SettingsRow
      label={e.email}
      sub={sub}
      tag={tag}
      trailing={
        e.primary ? undefined : (
          <Stack direction="horizontal" gap="2">
            {vm.canMakePrimary(e.id) ? (
              <Button label={t("settings.signIn.emails.makePrimary")} variant="ghost" size="sm" onPress={() => vm.askMakePrimary(e.id)} testID={`account-email-${id}-primary`} />
            ) : null}
            {e.verified ? null : (
              <Button label={t("settings.signIn.emails.verify")} variant="ghost" size="sm" onPress={() => void vm.resumeVerify(e.id)} testID={`account-email-${id}-verify`} />
            )}
            <Button label={t("settings.signIn.emails.remove")} variant="secondary" size="sm" onPress={() => vm.askRemoveEmail(e.id)} testID={`account-email-${id}-remove`} />
          </Stack>
        )
      }
      testID={`account-email-${id}`}
    />
  );
}

function PasskeyRow({ vm, passkey: p, canRemove }: { vm: AccountSecurityViewModel; passkey: AccountPasskey; canRemove: boolean }) {
  const t = useT();
  const fmt = useFormat();
  const created = fmt.timestamp(p.createdAt);
  return (
    <SettingsRow
      label={p.name ?? t("settings.signIn.passkeys.unnamed")}
      sub={p.lastUsedAt === undefined ? t("settings.signIn.passkeys.metaNever", { created }) : t("settings.signIn.passkeys.meta", { created, used: fmt.timestamp(p.lastUsedAt) })}
      trailing={
        <Stack direction="horizontal" gap="2">
          <Button label={t("settings.signIn.passkeys.rename")} variant="ghost" size="sm" onPress={() => vm.startRenamePasskey(p.id)} testID={`account-passkey-${p.id}-rename`} />
          {canRemove ? (
            <Button label={t("settings.signIn.passkeys.delete")} variant="secondary" size="sm" onPress={() => vm.askRemovePasskey(p.id)} testID={`account-passkey-${p.id}-delete`} />
          ) : null}
        </Stack>
      }
      testID={`account-passkey-${p.id}`}
    />
  );
}

/** The proof a change needs: the current password, or "Email me a code" then the code. */
function ProofFields({ vm, proof, email, onSubmit }: { vm: AccountSecurityViewModel; proof: ProofState; email: string; onSubmit: () => void }) {
  const t = useT();
  if (proof.need === "password") {
    return (
      <TextField
        label={t("settings.signIn.proof.passwordLabel")}
        value={proof.password}
        onChange={(v) => vm.setProofPassword(v)}
        secure
        autoFocus
        onSubmit={onSubmit}
        testID="account-proof-password"
      />
    );
  }
  if (proof.need !== "emailCode") return null;
  if (!proof.codeSent) {
    return (
      <Stack gap="2">
        <Text color="text.secondary">{t("settings.signIn.proof.codeBody", { email })}</Text>
        <Stack direction="horizontal">
          <Button label={t("settings.signIn.proof.sendCode")} icon="mail" variant="secondary" loading={proof.sending} onPress={() => void vm.sendProofCode()} testID="account-proof-send" />
        </Stack>
      </Stack>
    );
  }
  return (
    <Stack gap="2">
      <TextField
        label={t("settings.signIn.proof.codeLabel")}
        helperText={t("settings.signIn.proof.codeSent", { email })}
        value={proof.emailCode}
        onChange={(v) => vm.setProofCode(v)}
        keyboard="oneTimeCode"
        autoFocus
        onSubmit={onSubmit}
        testID="account-proof-code"
      />
      <Stack direction="horizontal">
        <Button label={t("settings.signIn.proof.resend")} variant="ghost" size="sm" loading={proof.sending} onPress={() => void vm.sendProofCode()} testID="account-proof-resend" />
      </Stack>
    </Stack>
  );
}

/** Live password hints (same rules as the server's local ones). */
function PasswordHints({ password, email }: { password: string; email: string }) {
  const t = useT();
  const long = [...password].length >= AccountSecurityLogic.PASSWORD_MIN;
  const noEmail = password !== "" && !AccountSecurityLogic.passwordContainsEmail(password, email);
  return (
    <Stack gap="0.5" testID="account-password-hints">
      <FieldNote text={t("auth.reset.hint.length")} tone={long ? "success" : "neutral"} />
      <FieldNote text={t("auth.reset.hint.email")} tone={noEmail ? "success" : "neutral"} />
      <FieldNote text={t("auth.reset.hint.common")} />
    </Stack>
  );
}

function BackupCodes({ codes, email, onCopy, onSaveFile }: { codes: string[]; email: string; onCopy: (text: string) => void; onSaveFile?: (name: string, text: string) => void }) {
  const t = useT();
  const [copied, setCopied] = useState(false);
  const text = codes.join("\n");
  return (
    <Stack gap="3" testID="account-backup-codes">
      <Text color="text.secondary">{t("settings.signIn.twoFactor.codesBody")}</Text>
      <Stack direction="horizontal" wrap gap="2" padding="3" radius="lg" border="border.subtle" background="bg.surface">
        {codes.map((c) => (
          <Stack key={c} paddingX="2">
            <Text mono selectable>
              {c}
            </Text>
          </Stack>
        ))}
      </Stack>
      <Stack direction="horizontal" gap="2" wrap>
        <Button
          label={copied ? t("settings.signIn.twoFactor.copied") : t("settings.signIn.twoFactor.copy")}
          icon={copied ? "check" : "copy"}
          variant="secondary"
          onPress={() => {
            onCopy(text);
            setCopied(true);
          }}
          testID="account-backup-codes-copy"
        />
        {onSaveFile === undefined ? null : (
          <Button
            label={t("settings.signIn.twoFactor.download")}
            icon="download"
            variant="secondary"
            onPress={() => {
              const f = backupCodesFile(codes, email, t("settings.signIn.twoFactor.fileHeading"));
              onSaveFile(f.name, f.text);
            }}
            testID="account-backup-codes-download"
          />
        )}
      </Stack>
    </Stack>
  );
}

function Actions({ children }: { children: ReactNode }) {
  return (
    <Stack direction="horizontal" justify="end" gap="2">
      {children}
    </Stack>
  );
}

/** The Account & sign-in sheets (add email, password, passkey name, two-step…); also Create account's "Set a password". */
export function SecuritySheet({ vm, state: s, onCopy, onSaveFile }: { vm: AccountSecurityViewModel; state: AccountSecurityState; onCopy: (text: string) => void; onSaveFile?: (name: string, text: string) => void }) {
  const t = useT();
  const tr = useTr();
  const sheet = s.sheet;
  const email = vm.primaryEmail;
  const close = () => vm.close();
  const cancel = <Button label={t("common.cancel")} variant="ghost" disabled={s.busy} onPress={close} testID="account-sheet-cancel" />;
  const error = s.error === undefined ? null : <FieldNote text={tr(s.error)} tone="danger" testID="account-sheet-error" />;
  let title = "";
  let body: ReactNode = null;
  switch (sheet?.kind) {
    case undefined:
      break;
    case "addEmail": {
      title = sheet.step === "email" ? t("settings.signIn.emails.addTitle") : t("settings.signIn.emails.codeTitle");
      body =
        sheet.step === "email" ? (
          <>
            <Text color="text.secondary">{t("settings.signIn.emails.addBody")}</Text>
            <TextField label={t("settings.signIn.emails.addLabel")} value={sheet.email} onChange={(v) => vm.setNewEmail(v)} keyboard="email" autoFocus onSubmit={() => void vm.sendEmailCode()} testID="account-add-email" />
            {error}
            <Actions>
              {cancel}
              <Button label={t("settings.signIn.emails.sendCode")} variant="primary" loading={s.busy} disabled={sheet.email.trim() === ""} onPress={() => void vm.sendEmailCode()} testID="account-add-email-send" />
            </Actions>
          </>
        ) : (
          <>
            <Text color="text.secondary">{t("settings.signIn.emails.codeBody", { email: sheet.email })}</Text>
            <TextField label={t("settings.signIn.emails.codeLabel")} value={sheet.code} onChange={(v) => vm.setEmailCode(v)} keyboard="oneTimeCode" autoFocus onSubmit={() => void vm.verifyEmail()} testID="account-add-email-code" />
            {error}
            <Stack direction="horizontal" justify="between" align="center" wrap gap="2">
              <Stack direction="horizontal" gap="1">
                <Button label={t("settings.signIn.emails.changeEmail")} variant="ghost" size="sm" onPress={() => vm.editNewEmail()} testID="account-add-email-change" />
                <Button label={t("settings.signIn.emails.resend")} variant="ghost" size="sm" onPress={() => void vm.sendEmailCode()} testID="account-add-email-resend" />
              </Stack>
              <Button label={t("settings.signIn.emails.verify")} variant="primary" loading={s.busy} disabled={sheet.code.length !== 6} onPress={() => void vm.verifyEmail()} testID="account-add-email-verify" />
            </Stack>
          </>
        );
      break;
    }
    case "removeEmail":
      title = t("settings.signIn.emails.removeTitle", { email: sheet.email });
      body = (
        <>
          <Text color="text.secondary">{t("settings.signIn.emails.removeBody")}</Text>
          {error}
          <Actions>
            {cancel}
            <Button label={t("settings.signIn.emails.remove")} variant="destructive" loading={s.busy} onPress={() => void vm.removeEmail()} testID="account-remove-email-confirm" />
          </Actions>
        </>
      );
      break;
    case "makePrimary":
      title = t("settings.signIn.emails.primaryTitle", { email: sheet.email });
      body = (
        <>
          <Text color="text.secondary">{t("settings.signIn.emails.primaryBody", { email: sheet.email, old: sheet.oldEmail })}</Text>
          {error}
          <Actions>
            {cancel}
            <Button label={t("settings.signIn.emails.makePrimary")} variant="primary" loading={s.busy} onPress={() => void vm.makePrimary()} testID="account-make-primary-confirm" />
          </Actions>
        </>
      );
      break;
    case "password": {
      const remove = sheet.mode === "remove";
      title = remove ? t("settings.signIn.password.removeTitle") : sheet.mode === "set" ? t("settings.signIn.password.setTitle") : t("settings.signIn.password.changeTitle");
      const save = () => void vm.savePassword();
      const ready = vm.proofReady && (remove || sheet.problem === undefined);
      body = (
        <>
          {remove ? <Text color="text.secondary">{t("settings.signIn.password.removeBody")}</Text> : null}
          <ProofFields vm={vm} proof={sheet.proof} email={email} onSubmit={save} />
          {remove ? null : (
            <>
              <TextField label={t("settings.signIn.password.new")} value={sheet.next} onChange={(v) => vm.setNewPassword(v)} secure onSubmit={save} testID="account-password-new" />
              <PasswordHints password={sheet.next} email={email} />
              <Stack gap="0.5">
                <Checkbox label={t("auth.reset.signOutOthers")} value={sheet.signOutOthers} onChange={(v) => vm.setSignOutOthers(v)} testID="account-password-sign-out-others" />
                <Text variant="caption" color="text.tertiary">
                  {t("auth.reset.signOutHint")}
                </Text>
              </Stack>
            </>
          )}
          {error}
          <Actions>
            {cancel}
            <Button
              label={remove ? t("settings.signIn.password.removeConfirm") : t("settings.signIn.password.save")}
              variant={remove ? "destructive" : "primary"}
              loading={s.busy}
              disabled={!ready}
              onPress={save}
              testID="account-password-save"
            />
          </Actions>
        </>
      );
      break;
    }
    case "passkeyName": {
      const rename = sheet.passkeyId !== undefined;
      title = rename ? t("settings.signIn.passkeys.renameTitle") : t("settings.signIn.passkeys.addTitle");
      body = (
        <>
          {rename ? null : <Text color="text.secondary">{t("settings.signIn.passkeys.addBody")}</Text>}
          <TextField
            label={rename ? t("settings.signIn.passkeys.renameLabel") : t("settings.signIn.passkeys.nameLabel")}
            placeholder={t("settings.signIn.passkeys.namePlaceholder")}
            value={sheet.name}
            onChange={(v) => vm.setPasskeyName(v)}
            autoFocus
            onSubmit={() => void vm.savePasskey()}
            testID="account-passkey-name"
          />
          {error}
          <Actions>
            {cancel}
            <Button
              label={rename ? t("common.save") : t("settings.signIn.continue")}
              variant="primary"
              loading={s.busy}
              disabled={rename && sheet.name.trim() === ""}
              onPress={() => void vm.savePasskey()}
              testID="account-passkey-save"
            />
          </Actions>
        </>
      );
      break;
    }
    case "removePasskey":
      title = t("settings.signIn.passkeys.deleteTitle");
      body = (
        <>
          <Text color="text.secondary">{t("settings.signIn.passkeys.deleteBody", { name: sheet.name === "" ? t("settings.signIn.passkeys.unnamed") : sheet.name })}</Text>
          {error}
          <Actions>
            {cancel}
            <Button label={t("settings.signIn.passkeys.delete")} variant="destructive" loading={s.busy} onPress={() => void vm.removePasskey()} testID="account-passkey-delete-confirm" />
          </Actions>
        </>
      );
      break;
    case "twoFactorSetup": {
      title = sheet.step === "codes" ? t("settings.signIn.twoFactor.codesTitle") : t("settings.signIn.twoFactor.setupTitle");
      if (sheet.step === "proof") {
        body = (
          <>
            <ProofFields vm={vm} proof={sheet.proof} email={email} onSubmit={() => void vm.continueTwoFactor()} />
            {error}
            <Actions>
              {cancel}
              <Button label={t("settings.signIn.continue")} variant="primary" loading={s.busy} disabled={!vm.proofReady} onPress={() => void vm.continueTwoFactor()} testID="account-two-factor-continue" />
            </Actions>
          </>
        );
      } else if (sheet.step === "scan" && sheet.setup !== undefined) {
        const secret = AccountSecurityLogic.groupSecret(sheet.setup.secret);
        body = (
          <>
            <Text color="text.secondary">{t("settings.signIn.twoFactor.scanBody")}</Text>
            <Stack direction="horizontal" gap="4" align="center" wrap>
              <QrCode value={sheet.setup.totpURI} label={t("settings.signIn.twoFactor.qrLabel")} testID="account-two-factor-qr" />
              <Stack gap="1" grow>
                <Text variant="footnote" color="text.secondary">
                  {t("settings.signIn.twoFactor.secretLabel")}
                </Text>
                <Text mono selectable weight="semibold" testID="account-two-factor-secret">
                  {secret}
                </Text>
                <Stack direction="horizontal">
                  <Button label={t("settings.signIn.twoFactor.copyKey")} icon="copy" variant="ghost" size="sm" onPress={() => onCopy(sheet.setup?.secret ?? "")} testID="account-two-factor-copy-key" />
                </Stack>
              </Stack>
            </Stack>
            <TextField label={t("settings.signIn.twoFactor.codeLabel")} value={sheet.code} onChange={(v) => vm.setTwoFactorCode(v)} keyboard="oneTimeCode" autoFocus onSubmit={() => void vm.confirmTwoFactor()} testID="account-two-factor-code" />
            {error}
            <Actions>
              {cancel}
              <Button label={t("settings.signIn.twoFactor.verify")} variant="primary" loading={s.busy} disabled={sheet.code.length !== 6} onPress={() => void vm.confirmTwoFactor()} testID="account-two-factor-verify" />
            </Actions>
          </>
        );
      } else if (sheet.step === "codes") {
        body = (
          <>
            <BackupCodes codes={vm.shownBackupCodes ?? []} email={email} onCopy={onCopy} {...(onSaveFile === undefined ? {} : { onSaveFile })} />
            <Actions>
              <Button label={t("settings.signIn.twoFactor.done")} variant="primary" onPress={close} testID="account-two-factor-done" />
            </Actions>
          </>
        );
      }
      break;
    }
    case "backupCodes":
      title = sheet.step === "codes" ? t("settings.signIn.twoFactor.codesTitle") : t("settings.signIn.twoFactor.newCodesTitle");
      body =
        sheet.step === "proof" ? (
          <>
            <Text color="text.secondary">{t("settings.signIn.twoFactor.newCodesBody")}</Text>
            <ProofFields vm={vm} proof={sheet.proof} email={email} onSubmit={() => void vm.regenerateBackupCodes()} />
            {error}
            <Actions>
              {cancel}
              <Button label={t("settings.signIn.twoFactor.newCodes")} variant="primary" loading={s.busy} disabled={!vm.proofReady} onPress={() => void vm.regenerateBackupCodes()} testID="account-backup-codes-generate" />
            </Actions>
          </>
        ) : (
          <>
            <BackupCodes codes={vm.shownBackupCodes ?? []} email={email} onCopy={onCopy} {...(onSaveFile === undefined ? {} : { onSaveFile })} />
            <Actions>
              <Button label={t("settings.signIn.twoFactor.done")} variant="primary" onPress={close} testID="account-backup-codes-done" />
            </Actions>
          </>
        );
      break;
    case "twoFactorOff":
      title = t("settings.signIn.twoFactor.offTitle");
      body = (
        <>
          <Text color="text.secondary">{t("settings.signIn.twoFactor.offBody")}</Text>
          <ProofFields vm={vm} proof={sheet.proof} email={email} onSubmit={() => void vm.turnOffTwoFactor()} />
          {error}
          <Actions>
            {cancel}
            <Button label={t("settings.signIn.twoFactor.turnOff")} variant="destructive" loading={s.busy} disabled={!vm.proofReady} onPress={() => void vm.turnOffTwoFactor()} testID="account-two-factor-off-confirm" />
          </Actions>
        </>
      );
      break;
  }
  return (
    <Sheet open={sheet !== undefined} onClose={close} title={title} closeLabel={t("common.close")} testID="account-security-sheet">
      <Stack gap="3">{body}</Stack>
    </Sheet>
  );
}
